/**
 * Australian phone number normalisation and formatting.
 * Storage: E.164 (+61...)
 * Display: Australian format (04XX XXX XXX or (0X) XXXX XXXX)
 */

/**
 * Normalise a phone number to E.164 (+61...) format.
 * Accepts: 0412345678, 61412345678, +61412345678, 0412 345 678, etc.
 * Returns null if not a valid AU number.
 */
export function normalizePhone(input: string | null | undefined): string | null {
  if (!input) return null
  const digits = input.replace(/[^\d+]/g, "")
  if (!digits) return null

  // +61...
  if (digits.startsWith("+61")) {
    const rest = digits.slice(3)
    if (/^\d{9}$/.test(rest)) return `+61${rest}`
    return null
  }
  // 61...
  if (digits.startsWith("61") && digits.length === 11) {
    return `+${digits}`
  }
  // 0...
  if (digits.startsWith("0") && digits.length === 10) {
    return `+61${digits.slice(1)}`
  }
  // 9-digit — assume dropped leading 0
  if (/^\d{9}$/.test(digits)) {
    return `+61${digits}`
  }

  return null
}

/**
 * Format an E.164 AU phone number for display.
 * Mobile:   +61412345678 → 0412 345 678
 * Landline: +61255551234 → (02) 5555 1234
 */
export function formatPhone(input: string | null | undefined): string {
  if (!input) return "—"
  const normalised = normalizePhone(input)
  if (!normalised) return input
  const body = normalised.slice(3) // strip +61
  const nationalNumber = `0${body}`

  // Mobile (04XX XXX XXX)
  if (nationalNumber.startsWith("04") || nationalNumber.startsWith("05")) {
    return `${nationalNumber.slice(0, 4)} ${nationalNumber.slice(4, 7)} ${nationalNumber.slice(7)}`
  }
  // Landline ((0X) XXXX XXXX)
  return `(${nationalNumber.slice(0, 2)}) ${nationalNumber.slice(2, 6)} ${nationalNumber.slice(6)}`
}

/**
 * Validate that a phone number is a valid Australian number.
 */
export function isValidAuPhone(input: string | null | undefined): boolean {
  return normalizePhone(input) !== null
}
